// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { ConfigResolver, getCacheKeyFromConfigRef } from "@msnews/config-resolver";
import { GetConfigTreeEmpty, GetConfigTreeError, GetConfigTreeNull, HrefNotValid } from "../AppErrors";
import { addWWPerfMarker, environment, logger } from "../wwCommon";
import { mockManager } from "@msnews/mock-config";
/**
 * Git config resolver class
 */
let configResolver;
/**
 * Dictionary of config index hrefs those are already visited.
 */
let visitedHrefs;
/**
 * Dictionary to store configs needed per experience type
 */
export const experienceGitConfigMap = new Map();
/**
 * Initialize Git config resolver environment.
 * @param {PageRequestTargetScope} currentTargetScope - Target scope with peregrine specific flights in pageExperiments
 * @param {ConfigResolver} resolver - ConfigResolver object to be used by unit tests.
 */
export function initializeGitResolverEnvironment(currentTargetScope, resolver) {
    visitedHrefs = {};
    if (resolver) {
        configResolver = resolver;
        return;
    }
    const configCacheProperties = {
        bundleHash: environment.getQueryParameter("bundleHash"),
        configHash: environment.getQueryParameter("configHash"),
        locHash: environment.getQueryParameter("locHash"),
        lastBundleHash: environment.getQueryParameter("lastBundleHash"),
        lastConfigHash: environment.getQueryParameter("lastConfigHash"),
        lastLocHash: environment.getQueryParameter("lastLocHash")
    };
    // Set up config service param contract
    const configServiceParameters = {
        buildVersion: environment.buildVersion,
        configServiceBaseUrl: environment.configServiceBaseUrl.toString()
    };
    // Set up Git config resolver contract
    const resolverEnvironment = {
        appType: environment.pcsApptype,
        configServiceParameters,
        targetScope: currentTargetScope,
        env: environment.env,
        featureFlags: environment.featureFlags,
        isThisWebWorkerThread: true,
        isWebWorkerPresent: true,
        queryParams: environment.getSearchParams(),
        appErrorCallback: logger.error.bind(logger),
        perfMarkerCallback: addWWPerfMarker,
        loggerCallback: logger.info.bind(logger),
        configCacheProperties,
        configOrigins: environment.configOrigins,
        mockManager: mockManager
    };
    // Initialize Git configs resolver with resolver environment
    configResolver = new ConfigResolver(resolverEnvironment);
}
/**
 * This is a recursive function to parse and download config tree.
 * @param configIndexHref: Relative Url of config Index href
 */
export function getGitConfigTree(configRef) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!configRef) {
            logger.error(Object.assign(Object.assign({}, HrefNotValid), { message: "Null ConfigRef in web worker resolver" }));
            return;
        }
        const mergedConfigsMap = new Map();
        try {
            const cacheKey = getCacheKeyFromConfigRef(configRef, environment.pcsApptype);
            if (visitedHrefs[cacheKey]) {
                return;
            }
            visitedHrefs[cacheKey] = true;
            // Load config from config-resolver package
            const mergedConfig = yield configResolver.getConfig(configRef);
            if (mergedConfig) {
                // Add this to internal map
                mergedConfigsMap.set(cacheKey, mergedConfig);
                // This map is used for as subscribable map by other files
                const experienceTypeKey = configRef.experienceType.toLowerCase();
                // form the dictionary using the experience type as key and list of configs as value,
                // for fast lookup when trying to find configs that match type + other criteria for
                // various WW prefetch operations.
                let configsOfThisType = experienceGitConfigMap.get(experienceTypeKey);
                if (!configsOfThisType) {
                    experienceGitConfigMap.set(experienceTypeKey, configsOfThisType = []);
                }
                configsOfThisType.push(mergedConfig);
            }
        }
        catch (e) {
            logger.error(Object.assign(Object.assign({}, GetConfigTreeError), { message: "getConfigTree threw error while getting Git config tree", pb: Object.assign(Object.assign({}, GetConfigTreeError.pb), { customMessage: `${JSON.stringify(configRef)}. Error: ${e}` }) }));
            return;
        }
        if (mergedConfigsMap.size === 0) {
            logger.error(Object.assign(Object.assign({}, GetConfigTreeEmpty), { message: "Didn't return config", pb: Object.assign(Object.assign({}, GetConfigTreeEmpty.pb), { customMessage: JSON.stringify(configRef) }) }));
            return;
        }
        const promises = [];
        mergedConfigsMap.forEach(resolvedConfig => {
            if (!resolvedConfig || !resolvedConfig.properties) {
                logger.error(Object.assign(Object.assign({}, GetConfigTreeNull), { message: "Returned null config", pb: Object.assign(Object.assign({}, GetConfigTreeNull.pb), { customMessage: JSON.stringify(configRef) }) }));
            }
            else {
                const configRefs = getChildConfigRefs(resolvedConfig);
                for (const childConfigRef of configRefs) {
                    // Recursive call to traverse the tree.
                    promises.push(getGitConfigTree(childConfigRef));
                }
            }
        });
        yield Promise.all(promises);
    });
}
/**
 * This function returns child configRefs
 * @param {ExperienceConfig} resolvedConfig: Experience resolved properties
 * @returns {Set<ConfigReference>} Array of Config Refs in the resolved config.
 */
export function getChildConfigRefs(resolvedConfig) {
    const configRefSet = new Set();
    if (resolvedConfig && resolvedConfig.properties) {
        try {
            addConfigRefFromObject(resolvedConfig.properties, configRefSet);
        }
        catch (error) {
            // No action!
            logger.info("WebWorker.getChildrenConfigIndexes config failed");
        }
    }
    return configRefSet;
}
/**
 * Adds all config ref found in the object to the given set.
 * @param obj The object to search in.
 * @param indicies The set of ConfigReference to add to.
 */
function addConfigRefFromObject(obj, indicies) {
    const { configRef } = obj;
    if (configRef && configRef.experienceType && configRef.instanceSrc) {
        indicies.add(configRef);
        // configRefs are usually immediately enclosed by a dedicated parent object
        // so further traversal of this immediate parent is not going to be fruitful.
        return;
    }
    for (const value of Object.values(obj)) {
        if (value && typeof (value) === "object") {
            addConfigRefFromObject(value, indicies);
        }
    }
}
/**
 * This overwrite (update) / write configs into cache
 * @returns true if configs are equal
 */
export function updateConfigsCache() {
    return __awaiter(this, void 0, void 0, function* () {
        return configResolver.updateConfigsCache();
    });
}
/**
 * To be called by unit tests in order to reset the state
 */
export function resetGitConfigResolver() {
    configResolver = null;
    visitedHrefs = null;
}
//# sourceMappingURL=wwGitConfigResolver.js.map